import { LayoutStyles, PptSlide, SlideContent } from 'apps/web/types/ppt-slide';
import { jsPDF } from 'jspdf';
import PptxGenJS from 'pptxgenjs';

import {
  SLIDE_LAYOUT,
  PDF_LAYOUT,
} from '../../constants/pptMakerDownloadFormat';

import { getContentAsString, processSlideContent } from './contentFormatter';

interface BaseLayoutOptions {
  fontId: string;
  textColor: string;
}

// Handles default text layout
function formatDefaultLayout(
  slide: PptxGenJS.Slide | jsPDF,
  content: SlideContent | undefined,
  options: BaseLayoutOptions,
  isPptx: boolean,
  isTitleLong: boolean,
): void {
  const { points, isBulleted, leadingString } = processSlideContent(content);

  if (points.length === 0 && !leadingString) return;

  if (isPptx) {
    const pptxSlide = slide as PptxGenJS.Slide;

    // Calculate y offset for points/bullets if leadingString exists
    let yOffset = isTitleLong
      ? SLIDE_LAYOUT.CONTENT.TEXT.Y_POSITION_LONG
      : SLIDE_LAYOUT.CONTENT.TEXT.Y_POSITION;

    // Render leadingString first, then add a blank line, then points/bullets
    if (leadingString) {
      pptxSlide.addText(leadingString, {
        x: SLIDE_LAYOUT.CONTENT.LEADING_TEXT.X_POSITION,
        y: yOffset,
        w: SLIDE_LAYOUT.CONTENT.LEADING_TEXT.WIDTH,
        fontFace: options.fontId,
        fontSize: SLIDE_LAYOUT.CONTENT.LEADING_TEXT.FONT_SIZE,
        align: SLIDE_LAYOUT.CONTENT.TEXT.ALIGN,
        valign: 'top',
        color: options.textColor,
      });
      // Estimate the height of the leadingString block and add extra space
      // 0.5 is a reasonable line height for a single line, but for multi-line, estimate lines
      const lines = leadingString.split('\n').length;
      yOffset += 0.75 * lines + 0.4; // 0.4 extra space for separation
    }

    if (isBulleted) {
      pptxSlide.addText(points.join('\n'), {
        x: SLIDE_LAYOUT.CONTENT.TEXT.X_POSITION,
        y: yOffset,
        w: SLIDE_LAYOUT.CONTENT.TEXT.WIDTH,
        fontFace: options.fontId,
        fontSize: SLIDE_LAYOUT.CONTENT.TEXT.FONT_SIZE,
        align: SLIDE_LAYOUT.CONTENT.TEXT.ALIGN,
        valign: 'top',
        color: options.textColor,
        bullet: { code: SLIDE_LAYOUT.CONTENT.BULLET.CODE },
      });
    } else if (points.length > 1) {
      const numberedPoints = points.map(
        (point, index) => `${index + 1}. ${point}`,
      );
      pptxSlide.addText(numberedPoints.join('\n'), {
        x: SLIDE_LAYOUT.CONTENT.TEXT.X_POSITION,
        y: yOffset,
        w: SLIDE_LAYOUT.CONTENT.TEXT.WIDTH,
        fontFace: options.fontId,
        fontSize: SLIDE_LAYOUT.CONTENT.TEXT.FONT_SIZE,
        align: SLIDE_LAYOUT.CONTENT.TEXT.ALIGN,
        valign: 'top',
        color: options.textColor,
      });
    } else if (points.length === 1) {
      pptxSlide.addText(points[0], {
        x: SLIDE_LAYOUT.CONTENT.TEXT.X_POSITION,
        y: yOffset,
        w: SLIDE_LAYOUT.CONTENT.TEXT.WIDTH,
        fontFace: options.fontId,
        fontSize: SLIDE_LAYOUT.CONTENT.TEXT.FONT_SIZE,
        align: SLIDE_LAYOUT.CONTENT.TEXT.ALIGN,
        valign: 'top',
        color: options.textColor,
      });
    }
  } else {
    const pdfDoc = slide as jsPDF;
    pdfDoc.setFont(options.fontId, 'normal');
    pdfDoc.setFontSize(PDF_LAYOUT.CONTENT.TEXT.FONT_SIZE);

    const baseY = isTitleLong
      ? PDF_LAYOUT.CONTENT.TEXT.Y_POSITION_LONG
      : PDF_LAYOUT.CONTENT.TEXT.Y_POSITION;
    const lineSpacing = 0.3;
    let currentY = baseY;

    // Render leadingString first if present
    if (leadingString) {
      const wrappedLeading = pdfDoc.splitTextToSize(
        leadingString,
        PDF_LAYOUT.CONTENT.TEXT.MAX_WIDTH,
      );
      pdfDoc.text(
        wrappedLeading,
        PDF_LAYOUT.CONTENT.TEXT.X_POSITION,
        currentY,
        {
          align: SLIDE_LAYOUT.CONTENT.TEXT.ALIGN,
          maxWidth: PDF_LAYOUT.CONTENT.TEXT.MAX_WIDTH,
          baseline: 'top',
        },
      );
      currentY += lineSpacing * wrappedLeading.length + 0.4;
    }

    if (isBulleted) {
      points.forEach((point) => {
        const textWidth = PDF_LAYOUT.CONTENT.TEXT.MAX_WIDTH;
        const wrappedText = pdfDoc.splitTextToSize(point, textWidth - 0.3);

        pdfDoc.text('\u2022', PDF_LAYOUT.CONTENT.TEXT.X_POSITION, currentY, {
          align: 'left',
          baseline: 'top',
        });

        pdfDoc.text(
          wrappedText,
          PDF_LAYOUT.CONTENT.TEXT.X_POSITION + 0.3,
          currentY,
          {
            align: 'left',
            baseline: 'top',
          },
        );

        currentY += lineSpacing * wrappedText.length + 0.1;
      });
    } else if (points.length > 1) {
      points.forEach((point, index) => {
        const numberText = `${index + 1}.`;
        const numberWidth = pdfDoc.getTextWidth(numberText);
        const textWidth = PDF_LAYOUT.CONTENT.TEXT.MAX_WIDTH;
        const wrappedText = pdfDoc.splitTextToSize(
          point,
          textWidth - numberWidth - 0.1,
        );

        pdfDoc.text(numberText, PDF_LAYOUT.CONTENT.TEXT.X_POSITION, currentY, {
          align: 'left',
          baseline: 'top',
        });

        pdfDoc.text(
          wrappedText,
          PDF_LAYOUT.CONTENT.TEXT.X_POSITION + numberWidth + 0.1,
          currentY,
          {
            align: 'left',
            baseline: 'top',
          },
        );

        currentY += lineSpacing * wrappedText.length + 0.1;
      });
    } else if (points.length === 1) {
      pdfDoc.text(points[0], PDF_LAYOUT.CONTENT.TEXT.X_POSITION, currentY, {
        align: SLIDE_LAYOUT.CONTENT.TEXT.ALIGN,
        maxWidth: PDF_LAYOUT.CONTENT.TEXT.MAX_WIDTH,
        baseline: 'top',
      });
    }
  }
}

// Handles centered focus text layout
function formatCenteredFocusLayout(
  slide: PptxGenJS.Slide | jsPDF,
  content: SlideContent | undefined,
  options: BaseLayoutOptions,
  isPptx: boolean,
): void {
  const contentStr = getContentAsString(content);

  if (isPptx) {
    const pptxSlide = slide as PptxGenJS.Slide;
    pptxSlide.addText(contentStr, {
      x: SLIDE_LAYOUT.CENTERED_FOCUS.X_POSITION,
      y: SLIDE_LAYOUT.CENTERED_FOCUS.Y_POSITION,
      w: SLIDE_LAYOUT.CENTERED_FOCUS.WIDTH,
      h: SLIDE_LAYOUT.CENTERED_FOCUS.HEIGHT,
      fontFace: options.fontId,
      fontSize: SLIDE_LAYOUT.CENTERED_FOCUS.FONT_SIZE,
      align: 'center',
      valign: 'top',
      color: options.textColor,
      bold: true,
    });
  } else {
    const pdfDoc = slide as jsPDF;
    pdfDoc.setFont(options.fontId, 'bold');
    pdfDoc.setFontSize(PDF_LAYOUT.CENTERED_FOCUS.FONT_SIZE);
    pdfDoc.text(
      contentStr,
      PDF_LAYOUT.CENTERED_FOCUS.X_POSITION,
      PDF_LAYOUT.CENTERED_FOCUS.Y_POSITION,
      {
        align: 'center',
        maxWidth: PDF_LAYOUT.CENTERED_FOCUS.MAX_WIDTH,
        baseline: 'top',
      },
    );
  }
}

function processBracketPattern(content: string): {
  items: string[];
  isNumbered: boolean;
} {
  // Process content with [text](*) or [text](number) format
  const bracketRegex = /\[(.*?)\](?:\(\*\)|\(\d+\))/g;
  const items = [];
  let match;
  let isNumbered = false;

  while ((match = bracketRegex.exec(content)) !== null) {
    items.push(match[1]);

    // Check if any point has a number format [text](number)
    if (/\[(.*?)\]\(\d+\)/.test(match[0])) {
      isNumbered = true;
    }
  }

  return { items, isNumbered };
}

// Handles grid-based layouts (quadrant, comparison)
function formatGridLayout(
  slide: PptxGenJS.Slide | jsPDF,
  content: SlideContent | undefined,
  options: BaseLayoutOptions,
  isPptx: boolean,
  layoutStyle: LayoutStyles,
): void {
  if (!content || !Array.isArray(content)) return;

  const isQuadrant = layoutStyle === LayoutStyles.QUADRANT;
  const isComparison = layoutStyle === LayoutStyles.COMPARISON;
  const isHorizontalSplit = layoutStyle === LayoutStyles.HORIZONTAL_SPLIT;
  const isVerticalSplit = layoutStyle === LayoutStyles.VERTICAL_SPLIT;

  // Determine grid dimensions
  const gridItems = content.map((item) => {
    if ('content' in item) {
      const processedPoints =
        'content' in item
          ? processBracketPattern(item.content)
          : { items: [], isNumbered: false };
      return {
        title: 'title' in item ? item.title : '',
        content: item.content,
        // For comparison layout, extract bullet points from bracket pattern
        bulletPoints: processedPoints.items,
        isNumbered: processedPoints.isNumbered,
      };
    }
    return { content: '', bulletPoints: [], isNumbered: false };
  });

  if (isPptx) {
    const pptxSlide = slide as PptxGenJS.Slide;
    const gridItemFontSize = SLIDE_LAYOUT.GRID.FONT_SIZE;

    if (isQuadrant) {
      // Render as 2x2 grid
      const gridPositions = [
        {
          x: SLIDE_LAYOUT.GRID.QUADRANT.LEFT_X_POSITION,
          y: SLIDE_LAYOUT.GRID.QUADRANT.TOP_Y_POSITION,
          w: SLIDE_LAYOUT.GRID.QUADRANT.WIDTH,
          h: SLIDE_LAYOUT.GRID.QUADRANT.HEIGHT,
        },
        {
          x: SLIDE_LAYOUT.GRID.QUADRANT.RIGHT_X_POSITION,
          y: SLIDE_LAYOUT.GRID.QUADRANT.TOP_Y_POSITION,
          w: SLIDE_LAYOUT.GRID.QUADRANT.WIDTH,
          h: SLIDE_LAYOUT.GRID.QUADRANT.HEIGHT,
        },
        {
          x: SLIDE_LAYOUT.GRID.QUADRANT.LEFT_X_POSITION,
          y: SLIDE_LAYOUT.GRID.QUADRANT.BOTTOM_Y_POSITION,
          w: SLIDE_LAYOUT.GRID.QUADRANT.WIDTH,
          h: SLIDE_LAYOUT.GRID.QUADRANT.HEIGHT,
        },
        {
          x: SLIDE_LAYOUT.GRID.QUADRANT.RIGHT_X_POSITION,
          y: SLIDE_LAYOUT.GRID.QUADRANT.BOTTOM_Y_POSITION,
          w: SLIDE_LAYOUT.GRID.QUADRANT.WIDTH,
          h: SLIDE_LAYOUT.GRID.QUADRANT.HEIGHT,
        },
      ];

      // Create grid container
      const gridX = SLIDE_LAYOUT.GRID.QUADRANT.CONTAINER.X_POSITION;
      const gridY = SLIDE_LAYOUT.GRID.QUADRANT.CONTAINER.Y_POSITION;
      const gridWidth = SLIDE_LAYOUT.GRID.QUADRANT.CONTAINER.WIDTH;
      const gridHeight = SLIDE_LAYOUT.GRID.QUADRANT.CONTAINER.HEIGHT;

      gridItems.slice(0, 4).forEach((item, index) => {
        pptxSlide.addText(item.content, {
          x: gridPositions[index].x,
          y: gridPositions[index].y,
          w: gridPositions[index].w,
          h: gridPositions[index].h,
          fontFace: options.fontId,
          fontSize: gridItemFontSize,
          color: options.textColor,
          align: 'left',
          valign: 'top',
          bold: false,
          margin: SLIDE_LAYOUT.GRID.QUADRANT.TEXT_MARGIN,
        });
      });
    } else if (isComparison || isHorizontalSplit) {
      // Render as horizontal split (2 columns)
      gridItems.slice(0, 2).forEach((item, index) => {
        // Add column title
        if (item.title) {
          pptxSlide.addText(item.title, {
            x:
              index === 0
                ? SLIDE_LAYOUT.GRID.COMPARISON.LEFT_X_POSITION
                : SLIDE_LAYOUT.GRID.COMPARISON.RIGHT_X_POSITION,
            y: SLIDE_LAYOUT.GRID.COMPARISON.TITLE_Y_POSITION,
            w: SLIDE_LAYOUT.GRID.COMPARISON.WIDTH,
            h: 0.5,
            fontFace: options.fontId,
            fontSize: SLIDE_LAYOUT.GRID.TITLE_FONT_SIZE,
            color: options.textColor,
            bold: true,
            align: 'center',
          });
        }

        if (item.bulletPoints && item.bulletPoints.length > 0) {
          // Join the bullet points with a newline for the PPTX format
          pptxSlide.addText(item.bulletPoints.join('\n'), {
            x:
              index === 0
                ? SLIDE_LAYOUT.GRID.COMPARISON.LEFT_X_POSITION
                : SLIDE_LAYOUT.GRID.COMPARISON.RIGHT_X_POSITION,
            y: SLIDE_LAYOUT.GRID.COMPARISON.Y_POSITION,
            w: SLIDE_LAYOUT.GRID.COMPARISON.WIDTH,
            h: 1.5,
            fontFace: options.fontId,
            fontSize: gridItemFontSize,
            color: options.textColor,
            bullet: { code: SLIDE_LAYOUT.CONTENT.BULLET.CODE },
          });
        } else {
          pptxSlide.addText(item.content, {
            x:
              index === 0
                ? SLIDE_LAYOUT.GRID.COMPARISON.LEFT_X_POSITION
                : SLIDE_LAYOUT.GRID.COMPARISON.RIGHT_X_POSITION,
            y: SLIDE_LAYOUT.GRID.COMPARISON.Y_POSITION,
            w: SLIDE_LAYOUT.GRID.COMPARISON.WIDTH,
            h: 1.5,
            fontFace: options.fontId,
            fontSize: gridItemFontSize,
            color: options.textColor,
            align: 'left',
          });
        }
      });
    } else if (isVerticalSplit) {
      // Render as vertical split (2 rows)
      gridItems.slice(0, 2).forEach((item, index) => {
        // Add section title
        if (item.title) {
          pptxSlide.addText(item.title, {
            x: SLIDE_LAYOUT.GRID.COMPARISON.LEFT_X_POSITION,
            y:
              index === 0
                ? SLIDE_LAYOUT.GRID.COMPARISON.TITLE_Y_POSITION
                : SLIDE_LAYOUT.GRID.COMPARISON.TITLE_Y_POSITION + 1.8,
            w: SLIDE_LAYOUT.GRID.COMPARISON.WIDTH * 2,
            h: 0.5,
            fontFace: options.fontId,
            fontSize: SLIDE_LAYOUT.GRID.TITLE_FONT_SIZE,
            color: options.textColor,
            bold: true,
            align: 'center',
          });
        }

        if (item.bulletPoints && item.bulletPoints.length > 0) {
          // Join the bullet points with a newline for the PPTX format
          pptxSlide.addText(item.bulletPoints.join('\n'), {
            x: SLIDE_LAYOUT.GRID.COMPARISON.LEFT_X_POSITION,
            y:
              index === 0
                ? SLIDE_LAYOUT.GRID.COMPARISON.Y_POSITION
                : SLIDE_LAYOUT.GRID.COMPARISON.Y_POSITION + 1.8,
            w: SLIDE_LAYOUT.GRID.COMPARISON.WIDTH * 2,
            h: 1.5,
            fontFace: options.fontId,
            fontSize: gridItemFontSize,
            color: options.textColor,
            bullet: { code: SLIDE_LAYOUT.CONTENT.BULLET.CODE },
          });
        } else {
          pptxSlide.addText(item.content, {
            x: SLIDE_LAYOUT.GRID.COMPARISON.LEFT_X_POSITION,
            y:
              index === 0
                ? SLIDE_LAYOUT.GRID.COMPARISON.Y_POSITION
                : SLIDE_LAYOUT.GRID.COMPARISON.Y_POSITION + 1.8,
            w: SLIDE_LAYOUT.GRID.COMPARISON.WIDTH * 2,
            h: 1.5,
            fontFace: options.fontId,
            fontSize: gridItemFontSize,
            color: options.textColor,
            align: 'left',
          });
        }
      });
    }
  } else {
    const pdfDoc = slide as jsPDF;
    const gridItemFontSize = PDF_LAYOUT.GRID.FONT_SIZE;

    if (isQuadrant) {
      const gridPositions = [
        {
          x: PDF_LAYOUT.GRID.QUADRANT.LEFT_X_POSITION,
          y: PDF_LAYOUT.GRID.QUADRANT.TOP_Y_POSITION,
          w: PDF_LAYOUT.GRID.QUADRANT.WIDTH,
          h: PDF_LAYOUT.GRID.QUADRANT.HEIGHT,
        },
        {
          x: PDF_LAYOUT.GRID.QUADRANT.RIGHT_X_POSITION,
          y: PDF_LAYOUT.GRID.QUADRANT.TOP_Y_POSITION,
          w: PDF_LAYOUT.GRID.QUADRANT.WIDTH,
          h: PDF_LAYOUT.GRID.QUADRANT.HEIGHT,
        },
        {
          x: PDF_LAYOUT.GRID.QUADRANT.LEFT_X_POSITION,
          y: PDF_LAYOUT.GRID.QUADRANT.BOTTOM_Y_POSITION,
          w: PDF_LAYOUT.GRID.QUADRANT.WIDTH,
          h: PDF_LAYOUT.GRID.QUADRANT.HEIGHT,
        },
        {
          x: PDF_LAYOUT.GRID.QUADRANT.RIGHT_X_POSITION,
          y: PDF_LAYOUT.GRID.QUADRANT.BOTTOM_Y_POSITION,
          w: PDF_LAYOUT.GRID.QUADRANT.WIDTH,
          h: PDF_LAYOUT.GRID.QUADRANT.HEIGHT,
        },
      ];

      // Create unified grid with dividers
      const gridX = PDF_LAYOUT.GRID.QUADRANT.CONTAINER.X_POSITION;
      const gridY = PDF_LAYOUT.GRID.QUADRANT.CONTAINER.Y_POSITION;
      const gridWidth = PDF_LAYOUT.GRID.QUADRANT.CONTAINER.WIDTH;
      const gridHeight = PDF_LAYOUT.GRID.QUADRANT.CONTAINER.HEIGHT;

      // Set drawing properties
      pdfDoc.setDrawColor(options.textColor);
      pdfDoc.setLineWidth(0.02);

      pdfDoc.setFont(options.fontId, 'normal');
      pdfDoc.setFontSize(gridItemFontSize);

      gridItems.slice(0, 4).forEach((item, index) => {
        const pos = gridPositions[index];
        pdfDoc.text(
          item.content,
          pos.x, // Left aligned position
          pos.y, // Start at the exact top with no padding
          {
            maxWidth: pos.w,
            baseline: 'top',
            align: 'left', // Left alignment
          },
        );
      });
    } else if (isComparison || isHorizontalSplit) {
      pdfDoc.setFontSize(PDF_LAYOUT.GRID.TITLE_FONT_SIZE);
      pdfDoc.setFont(options.fontId, 'bold');

      // Add column titles
      gridItems.slice(0, 2).forEach((item, index) => {
        if (item.title) {
          pdfDoc.text(
            item.title,
            index === 0
              ? PDF_LAYOUT.GRID.COMPARISON.CENTER_X_LEFT
              : PDF_LAYOUT.GRID.COMPARISON.CENTER_X_RIGHT,
            PDF_LAYOUT.GRID.COMPARISON.TITLE_Y_POSITION,
            {
              align: 'center',
              baseline: 'top',
            },
          );
        }
      });

      // Add column content
      pdfDoc.setFontSize(gridItemFontSize);
      pdfDoc.setFont(options.fontId, 'normal');

      gridItems.slice(0, 2).forEach((item, index) => {
        // Use bullet points extracted from bracket pattern if available
        const bulletPoints =
          item.bulletPoints && item.bulletPoints.length > 0
            ? item.bulletPoints
            : item.content.split('[*]');

        let yOffset = PDF_LAYOUT.GRID.COMPARISON.CONTENT_Y_POSITION;

        bulletPoints.forEach((point) => {
          if (point.trim()) {
            const trimmedPoint = point.trim();
            const textWidth = PDF_LAYOUT.GRID.COMPARISON.MAX_TEXT_WIDTH;
            const wrappedText = pdfDoc.splitTextToSize(trimmedPoint, textWidth);

            // Add the bullet
            pdfDoc.text(
              '\u2022',
              index === 0
                ? PDF_LAYOUT.GRID.COMPARISON.LEFT_X_POSITION
                : PDF_LAYOUT.GRID.COMPARISON.DIVIDER_X + 0.5,
              yOffset,
              {
                baseline: 'top',
              },
            );

            // Add the text with proper indentation
            pdfDoc.text(
              wrappedText,
              index === 0
                ? PDF_LAYOUT.GRID.COMPARISON.LEFT_X_POSITION +
                    PDF_LAYOUT.GRID.COMPARISON.BULLET_INDENT
                : PDF_LAYOUT.GRID.COMPARISON.DIVIDER_X +
                    PDF_LAYOUT.GRID.COMPARISON.BULLET_INDENT +
                    0.5,
              yOffset,
              {
                baseline: 'top',
              },
            );

            // Calculate spacing based on wrapped text lines plus extra gap
            yOffset += 0.3 * wrappedText.length + 0.1;
          }
        });
      });
    } else if (isVerticalSplit) {
      pdfDoc.setFontSize(PDF_LAYOUT.GRID.TITLE_FONT_SIZE);
      pdfDoc.setFont(options.fontId, 'bold');

      // Add section titles
      gridItems.slice(0, 2).forEach((item, index) => {
        if (item.title) {
          pdfDoc.text(
            item.title,
            5.0, // Use the center point (assuming PDF_LAYOUT.PAGE.WIDTH would be 10)
            index === 0
              ? PDF_LAYOUT.GRID.COMPARISON.TITLE_Y_POSITION
              : PDF_LAYOUT.GRID.COMPARISON.TITLE_Y_POSITION + 2.0,
            {
              align: 'center',
              baseline: 'top',
            },
          );
        }
      });

      // Add section content
      pdfDoc.setFontSize(PDF_LAYOUT.GRID.FONT_SIZE);
      pdfDoc.setFont(options.fontId, 'normal');

      gridItems.slice(0, 2).forEach((item, index) => {
        // Calculate position for each half
        const y =
          index === 0
            ? PDF_LAYOUT.GRID.COMPARISON.TITLE_Y_POSITION + 0.6
            : PDF_LAYOUT.GRID.COMPARISON.TITLE_Y_POSITION + 2.6;

        if (item.bulletPoints && item.bulletPoints.length > 0) {
          // Draw bullet points
          item.bulletPoints.forEach((point, pointIndex) => {
            // Draw bullet
            pdfDoc.setFont(options.fontId, 'bold');
            pdfDoc.text(
              '•',
              PDF_LAYOUT.GRID.COMPARISON.LEFT_X_POSITION,
              y + pointIndex * 0.3,
              {
                baseline: 'top',
              },
            );

            // Draw text
            pdfDoc.setFont(options.fontId, 'normal');
            pdfDoc.text(
              point,
              PDF_LAYOUT.GRID.COMPARISON.LEFT_X_POSITION + 0.3,
              y + pointIndex * 0.3,
              {
                maxWidth: 8.0, // Use a fixed width value instead of PAGE_WIDTH
                baseline: 'top',
              },
            );
          });
        } else {
          // Draw regular text
          pdfDoc.text(
            item.content,
            PDF_LAYOUT.GRID.COMPARISON.LEFT_X_POSITION,
            y,
            {
              maxWidth: 8.0, // Use a fixed width value instead of PAGE_WIDTH
              baseline: 'top',
              align: 'left',
            },
          );
        }
      });
    }
  }
}

function formatLeftContentLayout(
  slide: PptxGenJS.Slide | jsPDF,
  content: SlideContent | undefined,
  options: BaseLayoutOptions,
  isPptx: boolean,
): void {
  const { points, isBulleted } = processSlideContent(content);

  if (points.length === 0) return;

  if (isPptx) {
    const pptxSlide = slide as PptxGenJS.Slide;
    const fontSize = SLIDE_LAYOUT.LEFT_CONTENT.FONT_SIZE;

    if (isBulleted) {
      // Bullet points
      pptxSlide.addText(points.join('\n'), {
        x: SLIDE_LAYOUT.LEFT_CONTENT.X_POSITION,
        y: SLIDE_LAYOUT.LEFT_CONTENT.Y_POSITION,
        w: SLIDE_LAYOUT.LEFT_CONTENT.WIDTH,
        h: SLIDE_LAYOUT.LEFT_CONTENT.HEIGHT,
        fontFace: options.fontId,
        fontSize: fontSize,
        align: 'center',
        valign: 'top',
        color: options.textColor,
        bullet: { code: SLIDE_LAYOUT.CONTENT.BULLET.CODE },
      });
    } else if (points.length > 1) {
      // Numbered points
      const numberedPoints = points.map(
        (point, index) => `${index + 1}. ${point}`,
      );
      pptxSlide.addText(numberedPoints.join('\n'), {
        x: SLIDE_LAYOUT.LEFT_CONTENT.X_POSITION,
        y: SLIDE_LAYOUT.LEFT_CONTENT.Y_POSITION,
        w: SLIDE_LAYOUT.LEFT_CONTENT.WIDTH,
        h: SLIDE_LAYOUT.LEFT_CONTENT.HEIGHT,
        fontFace: options.fontId,
        fontSize: fontSize,
        align: 'center',
        valign: 'top',
        color: options.textColor,
      });
    } else {
      pptxSlide.addText(points[0], {
        x: SLIDE_LAYOUT.LEFT_CONTENT.X_POSITION,
        y: SLIDE_LAYOUT.LEFT_CONTENT.Y_POSITION,
        w: SLIDE_LAYOUT.LEFT_CONTENT.WIDTH,
        h: SLIDE_LAYOUT.LEFT_CONTENT.HEIGHT,
        fontFace: options.fontId,
        fontSize: fontSize,
        align: 'center',
        valign: 'top',
        color: options.textColor,
      });
    }
  } else {
    const pdfDoc = slide as jsPDF;
    const fontSize = PDF_LAYOUT.LEFT_CONTENT.FONT_SIZE;
    pdfDoc.setFont(options.fontId, 'normal');
    pdfDoc.setFontSize(fontSize);
    pdfDoc.setTextColor(options.textColor);

    const lineHeight = 0.3;
    let yPosition = PDF_LAYOUT.LEFT_CONTENT.Y_POSITION;

    if (isBulleted || points.length > 1) {
      points.forEach((point) => {
        if (isBulleted) {
          // Handle bullet points with proper spacing
          const wrappedText = pdfDoc.splitTextToSize(
            point,
            PDF_LAYOUT.LEFT_CONTENT.TEXT_WIDTH,
          );

          // Add bullet
          pdfDoc.text(
            '\u2022',
            PDF_LAYOUT.LEFT_CONTENT.BULLET_X_POSITION,
            yPosition,
            {
              align: 'left',
              baseline: 'top',
            },
          );

          // Add text with indentation
          pdfDoc.text(
            wrappedText,
            PDF_LAYOUT.LEFT_CONTENT.TEXT_INDENT,
            yPosition,
            {
              align: 'left',
              baseline: 'top',
            },
          );

          // Move to next item with spacing based on wrapped lines
          yPosition += lineHeight * wrappedText.length + 0.1;
        } else {
          // Regular multi-line text
          const wrappedText = pdfDoc.splitTextToSize(
            point,
            PDF_LAYOUT.LEFT_CONTENT.TEXT_WIDTH,
          );

          pdfDoc.text(
            wrappedText,
            PDF_LAYOUT.LEFT_CONTENT.TEXT_INDENT,
            yPosition,
            {
              align: 'center',
              baseline: 'top',
            },
          );

          // Move to next item with spacing based on wrapped lines
          yPosition += lineHeight * wrappedText.length + 0.1;
        }
      });
    } else {
      // Single point text
      const wrappedText = pdfDoc.splitTextToSize(
        points[0],
        PDF_LAYOUT.LEFT_CONTENT.TEXT_WIDTH,
      );

      pdfDoc.text(
        wrappedText,
        PDF_LAYOUT.LEFT_CONTENT.TEXT_INDENT,
        PDF_LAYOUT.LEFT_CONTENT.Y_POSITION,
        {
          align: 'center',
          baseline: 'top',
        },
      );
    }
  }
}

function formatRightContentLayout(
  slide: PptxGenJS.Slide | jsPDF,
  content: SlideContent | undefined,
  options: BaseLayoutOptions,
  isPptx: boolean,
): void {
  const { points, isBulleted } = processSlideContent(content);

  if (points.length === 0) return;

  if (isPptx) {
    const pptxSlide = slide as PptxGenJS.Slide;
    const fontSize = SLIDE_LAYOUT.RIGHT_CONTENT.FONT_SIZE;

    if (isBulleted) {
      // Handle bullet points
      pptxSlide.addText(points.join('\n'), {
        x: SLIDE_LAYOUT.RIGHT_CONTENT.X_POSITION,
        y: SLIDE_LAYOUT.RIGHT_CONTENT.Y_POSITION,
        w: SLIDE_LAYOUT.RIGHT_CONTENT.WIDTH,
        h: SLIDE_LAYOUT.RIGHT_CONTENT.HEIGHT,
        fontFace: options.fontId,
        fontSize: fontSize,
        align: 'center',
        valign: 'top',
        color: options.textColor,
        bullet: { code: SLIDE_LAYOUT.CONTENT.BULLET.CODE },
      });
    } else if (points.length > 1) {
      // Handle numbered list
      const numberedPoints = points.map(
        (point, index) => `${index + 1}. ${point}`,
      );
      pptxSlide.addText(numberedPoints.join('\n'), {
        x: SLIDE_LAYOUT.RIGHT_CONTENT.X_POSITION,
        y: SLIDE_LAYOUT.RIGHT_CONTENT.Y_POSITION,
        w: SLIDE_LAYOUT.RIGHT_CONTENT.WIDTH,
        h: SLIDE_LAYOUT.RIGHT_CONTENT.HEIGHT,
        fontFace: options.fontId,
        fontSize: fontSize,
        align: 'center',
        valign: 'top',
        color: options.textColor,
      });
    } else {
      pptxSlide.addText(points[0], {
        x: SLIDE_LAYOUT.RIGHT_CONTENT.X_POSITION,
        y: SLIDE_LAYOUT.RIGHT_CONTENT.Y_POSITION,
        w: SLIDE_LAYOUT.RIGHT_CONTENT.WIDTH,
        h: SLIDE_LAYOUT.RIGHT_CONTENT.HEIGHT,
        fontFace: options.fontId,
        fontSize: fontSize,
        align: 'center',
        valign: 'top',
        color: options.textColor,
      });
    }
  } else {
    const pdfDoc = slide as jsPDF;
    const fontSize = PDF_LAYOUT.RIGHT_CONTENT.FONT_SIZE;
    pdfDoc.setFont(options.fontId, 'normal');
    pdfDoc.setFontSize(fontSize);
    pdfDoc.setTextColor(options.textColor);

    const lineHeight = 0.3;
    let yPosition = PDF_LAYOUT.RIGHT_CONTENT.Y_POSITION;

    if (isBulleted || points.length > 1) {
      points.forEach((point) => {
        if (isBulleted) {
          // Handle bullet points with proper spacing
          const wrappedText = pdfDoc.splitTextToSize(
            point,
            PDF_LAYOUT.RIGHT_CONTENT.TEXT_WIDTH,
          );

          // Add bullet
          pdfDoc.text(
            '\u2022',
            PDF_LAYOUT.RIGHT_CONTENT.BULLET_X_POSITION,
            yPosition,
            {
              align: 'left',
              baseline: 'top',
            },
          );

          // Add text with indentation
          pdfDoc.text(
            wrappedText,
            PDF_LAYOUT.RIGHT_CONTENT.TEXT_INDENT,
            yPosition,
            {
              align: 'left',
              baseline: 'top',
            },
          );

          // Move to next item with spacing based on wrapped lines
          yPosition += lineHeight * wrappedText.length + 0.1;
        } else {
          // Regular multi-line text
          const wrappedText = pdfDoc.splitTextToSize(
            point,
            PDF_LAYOUT.RIGHT_CONTENT.TEXT_WIDTH,
          );

          pdfDoc.text(
            wrappedText,
            PDF_LAYOUT.RIGHT_CONTENT.TEXT_INDENT,
            yPosition,
            {
              align: 'center',
              baseline: 'top',
            },
          );

          // Move to next item with spacing based on wrapped lines
          yPosition += lineHeight * wrappedText.length + 0.1;
        }
      });
    } else {
      // Single point text
      const wrappedText = pdfDoc.splitTextToSize(
        points[0],
        PDF_LAYOUT.RIGHT_CONTENT.TEXT_WIDTH,
      );

      pdfDoc.text(
        wrappedText,
        PDF_LAYOUT.RIGHT_CONTENT.TEXT_INDENT,
        PDF_LAYOUT.RIGHT_CONTENT.Y_POSITION,
        {
          align: 'center',
          baseline: 'top',
        },
      );
    }
  }
}

// Main function to apply layout based on slide configuration
export function applySlideLayout(
  slide: PptxGenJS.Slide | jsPDF,
  pptSlide: PptSlide,
  options: BaseLayoutOptions,
  isPptx: boolean,
  isTitleLong: boolean,
): void {
  const { layout, content } = pptSlide;

  switch (layout) {
    case LayoutStyles.CENTERED_FOCUS:
      formatCenteredFocusLayout(slide, content, options, isPptx);
      break;
    case LayoutStyles.QUADRANT:
    case LayoutStyles.COMPARISON:
    case LayoutStyles.HORIZONTAL_SPLIT:
    case LayoutStyles.VERTICAL_SPLIT:
      formatGridLayout(slide, content, options, isPptx, layout);
      break;
    case LayoutStyles.LEFT_CONTENT:
      formatLeftContentLayout(slide, content, options, isPptx);
      break;
    case LayoutStyles.RIGHT_CONTENT:
      formatRightContentLayout(slide, content, options, isPptx);
      break;
    case LayoutStyles.DEFAULT:
    default:
      formatDefaultLayout(slide, content, options, isPptx, isTitleLong);
      break;
  }
}
